import { useEffect, useState } from 'react';

/**
 * Whether the given faces have loaded - for anything that MEASURES text before
 * it draws: canvas `measureText` (the mindmap's pills), or mermaid sizing a
 * node box to its label. Measured in a fallback face, every box comes out the
 * wrong size and the real text then overflows the box it was drawn for.
 *
 * Each face is a CSS `font` shorthand, the form `document.fonts.load` takes:
 * `'600 14px "Inter Variable"'`. They are requested EXPLICITLY, because
 * `document.fonts.ready` alone resolves before a face nothing on the page has
 * used yet (JetBrains Mono on a page of Inter) is even asked for.
 *
 * True wherever there is no font API at all (jsdom, SSR): a diagram measured
 * in the fallback face beats no diagram.
 */
export function useFontsReady(faces: readonly string[]): boolean {
  const [ready, setReady] = useState(false);
  // `faces` is rebuilt every render; its CONTENTS are the real dependency.
  const key = faces.join('|');

  useEffect(() => {
    const fonts = typeof document !== 'undefined' ? document.fonts : undefined;
    if (!fonts?.load) {
      setReady(true);
      return;
    }
    let live = true;
    Promise.all(key.split('|').map(face => fonts.load(face)))
      .catch(() => undefined)
      .then(() => live && setReady(true));
    return () => {
      live = false;
    };
  }, [key]);

  return ready;
}
